import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Send, Wand2 } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import { Textarea } from '../../../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../ui/dialog';
import { epicengageSupabase } from '../../../../../integrations/supabase/client'
import { toast } from 'sonner';
import type { EmailTemplateV2 } from '../../../../../types/epicengage';

interface StructuredBody {
  greeting?: string;
  content?: string;
  cta_text?: string;
  cta_url?: string;
  footer?: string;
}

export default function TemplateEditor() {
  const { templatid } = useParams<{ templatid: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<EmailTemplateV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    greeting: 'Hi {{first_name}},',
    content: '',
    cta_text: '',
    cta_url: '',
    footer: 'Best regards,\nThe Team'
  });

  useEffect(() => {
    if (!templatid) return;

    const fetchTemplate = async () => {
      const { data, error } = await epicengageSupabase
        .from('email_templates_v2')
        .select('*')
        .eq('id', templatid)
        .single();

      if (error) {
        toast.error('Template not found');
        navigate('/clientpanel/enage/templates');
        return;
      }

      setTemplate(data as EmailTemplateV2);
      
      const body = (data.body_json as StructuredBody) || {};
      setFormData({
        name: data.name,
        subject: data.subject,
        greeting: body.greeting || 'Hi {{first_name}},',
        content: body.content || '',
        cta_text: body.cta_text || '',
        cta_url: body.cta_url || '',
        footer: body.footer || 'Best regards,\nThe Team'
      });
      
      setLoading(false);
    };

    fetchTemplate();
  }, [templatid, navigate]);

  const generateHtml = (): string => {
    const { greeting, content, cta_text, cta_url, footer } = formData;
    
    // Convert double newlines to paragraph breaks, single newlines to <br>
    const formatContent = (text: string): string => {
      if (!text) return '';
      
      // Split by double newlines (paragraph breaks)
      const paragraphs = text.split(/\n\s*\n/);
      
      return paragraphs.map(para => {
        // Within each paragraph, convert single newlines to <br>
        const lines = para.split('\n').map(line => line.trim()).filter(Boolean);
        return lines.length > 0 ? `<p style="margin: 0 0 16px 0;">${lines.join('<br>')}</p>` : '';
      }).filter(Boolean).join('');
    };
    
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${formData.subject}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
    .container { max-width: 600px; margin: 0 auto; background: white; }
    .content { padding: 40px; }
    .content p { margin: 0 0 16px 0; }
    .cta-button { display: inline-block; background-color: #3b82f6; color: white !important; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 20px 0; }
    .footer { padding: 20px 40px; border-top: 1px solid #eee; font-size: 14px; color: #666; }
    .unsubscribe { margin-top: 20px; font-size: 12px; color: #999; }
    .unsubscribe a { color: #999; }
  </style>
</head>
<body>
  <div class="container">
    <div class="content">
      <p style="margin: 0 0 16px 0;">${greeting}</p>
      ${formatContent(content)}
      ${cta_text && cta_url ? `<a href="${cta_url}" class="cta-button">${cta_text}</a>` : ''}
      <p style="margin: 16px 0 0 0; white-space: pre-line;">${footer}</p>
    </div>
    <div class="footer">
      <div class="unsubscribe">
        <a href="{{unsubscribe_url}}">Unsubscribe</a> from these emails
      </div>
    </div>
  </div>
</body>
</html>`;
  };

  const handleSave = async () => {
    if (!templatid) return;
    
    setSaving(true);
    
    const html = generateHtml();
    const bodyJson = {
      greeting: formData.greeting,
      content: formData.content,
      cta_text: formData.cta_text,
      cta_url: formData.cta_url,
      footer: formData.footer
    } as Record<string, string>;

    const { error } = await epicengageSupabase
      .from('email_templates_v2')
      .update({
        name: formData.name,
        subject: formData.subject,
        body_json: bodyJson,
        html_cache: html
      })
      .eq('id', templatid);

    if (error) {
      toast.error('Failed to save: ' + error.message);
    } else {
      toast.success('Template saved');
    }
    
    setSaving(false);
  };

  if (loading) {
    return (
      <V1Layout>
        <div className="p-8 text-muted-foreground">Loading...</div>
      </V1Layout>
    );
  }

  return (
    <V1Layout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/clientpanel/enage/templates">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">Edit Template</h1>
          </div>
          <div className="flex gap-2">
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                <DialogHeader>
                  <DialogTitle>Email Preview</DialogTitle>
                </DialogHeader>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted px-4 py-2 text-sm">
                    <strong>Subject:</strong> {formData.subject}
                  </div>
                  <iframe
                    srcDoc={generateHtml()}
                    className="w-full h-[500px] border-0"
                    title="Email Preview"
                  />
                </div>
              </DialogContent>
            </Dialog>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Editor */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Template Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Template Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                    placeholder="Welcome Email"
                  />
                </div>
                <div>
                  <Label>Subject Line</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData(f => ({ ...f, subject: e.target.value }))}
                    placeholder="Welcome to our community!"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use {"{{first_name}}"} for personalization
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Email Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Greeting</Label>
                  <Input
                    value={formData.greeting}
                    onChange={(e) => setFormData(f => ({ ...f, greeting: e.target.value }))}
                    placeholder="Hi {{first_name}},"
                  />
                </div>
                <div>
                  <Label>Body</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData(f => ({ ...f, content: e.target.value }))}
                    placeholder="Write your email content here..."
                    rows={8}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CTA Button Text</Label>
                    <Input
                      value={formData.cta_text}
                      onChange={(e) => setFormData(f => ({ ...f, cta_text: e.target.value }))}
                      placeholder="Shop Now"
                    />
                  </div>
                  <div>
                    <Label>CTA Button URL</Label>
                    <Input
                      value={formData.cta_url}
                      onChange={(e) => setFormData(f => ({ ...f, cta_url: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div>
                  <Label>Footer / Sign-off</Label>
                  <Textarea
                    value={formData.footer}
                    onChange={(e) => setFormData(f => ({ ...f, footer: e.target.value }))}
                    placeholder="Best regards,&#10;The Team"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Live Preview */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Live Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden bg-gray-100">
                  <div className="bg-white mx-auto max-w-[400px] shadow-lg">
                    <iframe
                      srcDoc={generateHtml()}
                      className="w-full h-[600px] border-0"
                      title="Live Preview"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </V1Layout>
  );
}
